import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	VStack,
	Text,
	useToast,
	Link,
	Icon,
} from '@chakra-ui/react';
import { FaArrowLeft } from 'react-icons/fa';
import { requestPasswordReset } from '../../../Api/authApi';
import {
	useAuthFlow,
	AUTH_MODES,
} from '../../../services/Contexts/AuthFlowContext';
import {
	authFormInputProps,
	authFormLabelProps,
	authFormLinkProps,
	authFormMutedTextProps,
	authFormPrimaryButtonProps,
	authFormShellProps,
} from './authFormTheme';

const RequestPasswordReset = ({ showTitle = true }) => {
	const [email, setEmail] = useState('');
	const [cooldown, setCooldown] = useState(0);
	const { setMode } = useAuthFlow();
	const toast = useToast();

	useEffect(() => {
		if (!cooldown) return;

		const timer = setInterval(() => {
			setCooldown((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [cooldown]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const data = await requestPasswordReset(email);
			toast({
				title: 'Password Reset Sent',
				description: data.msg || 'Password reset email sent successfully!',
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
			setCooldown(40);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.msg ||
				error?.message ||
				'Failed to send password reset email. Please try again.';
			toast({
				title: 'Reset Password Request',
				description: errorMessage,
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			setCooldown(40);
		}
	};

	return (
		<Box {...authFormShellProps}>
			<VStack spacing={5} align='stretch'>
				<Button
					variant='ghost'
					size='sm'
					alignSelf='flex-start'
					color='gray.600'
					leftIcon={<Icon as={FaArrowLeft} />}
					onClick={() => setMode(AUTH_MODES.SIGN_IN)}
					_hover={{ bg: 'gray.100', color: 'gray.800' }}>
					Back to sign in
				</Button>

				{showTitle && (
					<>
						<Text fontSize='xl' fontWeight='bold' color='gray.800' textAlign='center'>
							Forgot password
						</Text>
						<Text color='gray.600' fontSize='md' textAlign='center' mb={0}>
							Enter your email and we&apos;ll send you a reset link.
						</Text>
					</>
				)}

				<form onSubmit={handleSubmit}>
					<VStack spacing={5} align='stretch'>
						<FormControl isRequired>
							<FormLabel {...authFormLabelProps}>Email</FormLabel>
							<Input
								type='email'
								placeholder='you@company.com'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								{...authFormInputProps}
							/>
						</FormControl>

						<Button
							type='submit'
							{...authFormPrimaryButtonProps}
							isDisabled={cooldown > 0 || !email.trim()}>
							{cooldown > 0 ? `Resend in ${cooldown}s` : 'Send reset link'}
						</Button>
					</VStack>
				</form>

				<Text {...authFormMutedTextProps}>
					Remember your password?{' '}
					<Link
						{...authFormLinkProps}
						cursor='pointer'
						onClick={() => setMode(AUTH_MODES.SIGN_IN)}>
						Sign in
					</Link>
				</Text>
			</VStack>
		</Box>
	);
};

export default RequestPasswordReset;
